import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import type { DealOptionGroup } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsModel';
import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { useInclusionDraftsContext, usePricingOptionNamesContext } from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS, optionLabel as getOptionLabel } from '../../constants';

import DealOptionInclusionsView from './DealOptionInclusionsView';
import InclusionsEditView from './InclusionsEditView';
import { defaultApplyTo, deriveOpportunityCurrency, getUniqueInclusionAssets } from './inclusionsModel';
import type { DealOptionInclusionGroup } from './inclusionsModel';

interface Props {
  offerId?: string;
}

type ViewMode = 'edit' | 'list';

export default function InclusionsTab({ offerId }: Props) {
  const { data, isLoading, error: relatedError } = useGetOpportunityRelated(offerId);
  const { drafts, addDraft, updateDraft, removeDraft, replaceOptionDrafts } = useInclusionDraftsContext();
  const { optionNames } = usePricingOptionNamesContext();
  const { enqueueSnackbar } = useSnackbar();

  const [view, setView] = useState<ViewMode>('edit');
  const [activeOption, setActiveOption] = useState(0);

  const currency = useMemo(() => deriveOpportunityCurrency(data?.deals ?? []), [data?.deals]);
  const knownAssets = useMemo(() => getUniqueInclusionAssets(data?.inclusions ?? []), [data?.inclusions]);

  // PLACEHOLDER until Package Options (XTRNT-1004) ships - see docs/business-rules.md#draft-session-state.
  const dealOptionGroups: Array<DealOptionGroup> = [];
  const dealOptionInclusionGroups: Array<DealOptionInclusionGroup> = [];
  // See docs/business-rules.md#draft-session-state.
  const assetChipLabels = useMemo(
    () => Array.from(new Set(knownAssets.map((asset) => asset.name).filter((name): name is string => !!name))),
    [knownAssets],
  );

  const optionDrafts = drafts.filter((draft) => draft.pricingOptionIndex === activeOption);

  const addBlankInclusion = (prefill: { name?: string; type?: string } = {}) => {
    addDraft({
      pricingOptionIndex: activeOption,
      name: prefill.name ?? '',
      description: '',
      type: prefill.type,
      applies: undefined,
      validFor: [],
      prv: undefined,
      assetLink: { mode: 'new', newAssetName: prefill.name },
      applyTo: defaultApplyTo(),
    });
  };

  const handleCopyFromFirstOption = () => {
    const sourceDrafts = drafts.filter((draft) => draft.pricingOptionIndex === 0);
    replaceOptionDrafts(activeOption, sourceDrafts);
    enqueueSnackbar(
      `Copied over inclusions from ${getOptionLabel(optionNames, 0)} to ${getOptionLabel(optionNames, activeOption)}`,
      {
        variant: 'info',
      },
    );
  };

  if (isLoading) {
    return null;
  }

  return (
    <TabPanelShell
      title="Inclusions"
      subtitle="Add inclusions and see what each one is worth - the lever that lifts PRV without cutting price."
      actions={
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_event, next) => next && setView(next)}>
          <ToggleButton value="edit">Edit</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Stack spacing={2}>
        {relatedError && (
          <Alert severity="error">
            Unable to load synced assets. Inclusion assets, known deal options, and the opportunity currency may be
            incomplete.
          </Alert>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Inclusion assets
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {assetChipLabels.length === 0 ? (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                No inclusion assets yet - they are created when you add an inclusion
              </Typography>
            ) : (
              assetChipLabels.map((label) => <Chip key={label} label={label} size="small" />)
            )}
          </Stack>
        </Box>

        <Divider />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeOption}
          onChange={(_event, next) => next !== null && setActiveOption(next)}
        >
          {PRICING_OPTION_LABELS.map((_, index) => (
            <ToggleButton key={index} value={index}>
              {getOptionLabel(optionNames, index)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {view === 'edit' && (
          <InclusionsEditView
            currency={currency}
            dealOptionGroups={dealOptionGroups}
            drafts={optionDrafts}
            knownAssets={knownAssets}
            onAddInclusion={addBlankInclusion}
            onCopyFromFirstOption={activeOption > 0 ? handleCopyFromFirstOption : undefined}
            onRemoveDraft={removeDraft}
            onUpdateDraft={updateDraft}
            sourceOptionLabel={getOptionLabel(optionNames, 0)}
            targetOptionLabel={getOptionLabel(optionNames, activeOption)}
          />
        )}

        {view === 'list' && (
          <DealOptionInclusionsView
            groups={dealOptionInclusionGroups}
            currency={currency}
            optionLabel={getOptionLabel(optionNames, activeOption)}
          />
        )}
      </Stack>
    </TabPanelShell>
  );
}
